import * as React from "react";
import { css } from "@emotion/core";
import { Dialog, Button, ButtonGroup, Checkbox } from "@blueprintjs/core";
import { DownloadItem } from "../../hooks/useDownloads";
import { sanitizeFilename } from "../../utils";

export interface DownloadButtonProps {
  onClick: () => void;
  text: string;
  hasSelection: boolean;
  selectedCount: number;
  totalCount: number;
}

interface DownloadSelectorProps {
  downloads: DownloadItem[];
  isLoading?: boolean;
  variant?: "dialog" | "inline";
  isOpen?: boolean;
  onClose?: () => void;
  title?: string;
  hideButton?: boolean;
  onButtonProps?: (props: DownloadButtonProps) => void;
}

export const DownloadSelector: React.FC<DownloadSelectorProps> = ({
  downloads,
  isLoading = false,
  variant = "inline",
  isOpen = false,
  onClose,
  title,
  hideButton = false,
  onButtonProps,
}) => {
  const [checked, setChecked] = React.useState<Record<string, boolean>>({});

  const downloadDocument = (id: string, filename: string) => {
    const a = document.createElement("a");
    a.href = `/api/v1/auth/aws/download?key=${encodeURIComponent(id)}`;
    if (filename) {
      a.download = sanitizeFilename(filename);
    }
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const selectedIds = React.useMemo(() => {
    return downloads
      .filter((download) => checked[download.id] === true)
      .map((download) => download.id);
  }, [downloads, checked]);

  const selectedCount = React.useMemo(
    () => Object.values(checked).filter((v) => v).length,
    [checked]
  );

  const hasSelection = React.useMemo(() => selectedCount > 0, [selectedCount]);

  const downloadAll = React.useCallback(() => {
    const DELAY_MS = 150;
    downloads.forEach((download, index) => {
      setTimeout(() => {
        downloadDocument(download.id, download.label);
      }, index * DELAY_MS);
    });
  }, [downloads]);

  const downloadSelected = React.useCallback(() => {
    const DELAY_MS = 150;
    const selectedDownloads = downloads.filter((download) =>
      selectedIds.includes(download.id)
    );
    selectedDownloads.forEach((download, index) => {
      setTimeout(() => {
        downloadDocument(download.id, download.label);
      }, index * DELAY_MS);
    });
  }, [downloads, selectedIds]);

  const handleClose = () => {
    setChecked({});
    if (onClose) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (onButtonProps && variant === "inline" && downloads.length > 0) {
      onButtonProps({
        onClick: hasSelection ? downloadSelected : downloadAll,
        text: hasSelection
          ? `Download Selected (${selectedCount})`
          : `Download All (${downloads.length})`,
        hasSelection,
        selectedCount,
        totalCount: downloads.length,
      });
    }
  }, [
    onButtonProps,
    variant,
    downloads.length,
    hasSelection,
    selectedCount,
    downloadSelected,
    downloadAll,
  ]);

  const renderContent = () => {
    if (isLoading && downloads.length === 0) {
      return (
        <div
          css={css`
            color: var(--neutral-3);
            font-size: 0.875rem;
          `}
        >
          Loading...
        </div>
      );
    }

    return (
      <>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            padding: 0.25rem 0;
          `}
        >
          {downloads.map((download) => (
            <div
              key={download.id}
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;
              `}
            >
              <Checkbox
                checked={checked[download.id] === true}
                onChange={(event) => {
                  setChecked({
                    ...checked,
                    [download.id]: event.target.checked,
                  });
                }}
                css={css`
                  margin: 0;
                `}
              >
                <span
                  css={css`
                    ${variant === "inline"
                      ? `
                      flex: 1;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    `
                      : ""}
                  `}
                >
                  {download.label}
                </span>
              </Checkbox>
              <Button
                icon="download"
                onClick={() => {
                  downloadDocument(download.id, download.label);
                }}
              />
            </div>
          ))}
        </div>
        {variant === "inline" &&
          downloads.length > 0 &&
          !hideButton &&
          !onButtonProps && (
            <div
              css={css`
                margin-top: 0.5rem;
                display: flex;
                justify-content: flex-end;
              `}
            >
              <Button
                icon="download"
                onClick={hasSelection ? downloadSelected : downloadAll}
                intent="primary"
                text={
                  hasSelection
                    ? `Download Selected (${selectedCount})`
                    : `Download All (${downloads.length})`
                }
              />
            </div>
          )}
      </>
    );
  };

  if (variant === "dialog") {
    return (
      <Dialog
        className="bp6-dark"
        css={css`
          padding-bottom: 0;
          width: auto;
          max-width: 80vw;
          min-width: 512px;
        `}
        isOpen={isOpen}
        onClose={handleClose}
        title={title || `${downloads.length} Downloads`}
      >
        <div
          css={css`
            max-height: 50vh;
            padding: 1rem;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
          `}
        >
          {renderContent()}
        </div>
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            padding: 1rem;
            border-top: 1px solid var(--neutral-6);
          `}
        >
          <ButtonGroup>
            <Button text="Cancel" onClick={handleClose} />
            {downloads.length > 0 && (
              <Button
                icon="download"
                onClick={hasSelection ? downloadSelected : downloadAll}
                text={
                  hasSelection
                    ? `Download Selected (${selectedCount})`
                    : `Download All (${downloads.length})`
                }
                intent="primary"
              />
            )}
          </ButtonGroup>
        </div>
      </Dialog>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      `}
    >
      {renderContent()}
    </div>
  );
};
